import { skuKeyLabel, type SkuSeries } from "@estelle/domain";
import { useMemo } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { formatCount } from "@/lib/chart-format";
import { formatMoney, sumMoney } from "@/modules/gst/format";
import {
  pickColour,
  sharePercent,
  shareText,
  skuLabel,
  skuProduct,
} from "@/modules/reports/skuTrends";

interface ShareRow {
  key: string;
  label: string;
  product: string;
  units: number;
  amount: string;
  colour: string | null;
}

/**
 * How much of everything sold the chosen products make up (#678): their units
 * and their sales, each against the total for all SKUs in the dates.
 *
 * Two bars and a table, not a pie: the lines above are for the trend over time,
 * and what this answers is "how big is each, and how big are they together",
 * which wants exact figures and one straight scale to compare against.
 */
export function ShareOfTotal({ series }: { series: SkuSeries }) {
  const { rows, together, rest } = useMemo(() => {
    const byKey = new Map(
      series.items.map((item) => [item.key, item] as const),
    );
    const rows: ShareRow[] = series.picked.map((key, index) => {
      const item = byKey.get(key);
      return {
        key,
        label: item ? skuLabel(item) : skuKeyLabel(key),
        product: item ? skuProduct(item) : "No sales in these dates",
        units: item?.units ?? 0,
        amount: item?.amount ?? "0.00",
        colour: pickColour(index),
      };
    });
    const units = rows.reduce((sum, row) => sum + row.units, 0);
    const amount = sumMoney(rows.map((row) => row.amount));
    return {
      rows,
      together: { units, amount },
      rest: {
        units: series.totals.units - units,
        amount: sumMoney([series.totals.amount, `-${amount}`]),
      },
    };
  }, [series]);

  const totalUnits = series.totals.units;
  const totalAmount = series.totals.amount;

  return (
    <Card className="gap-4">
      <CardContent className="flex flex-col gap-3">
        <div>
          <h3 className="text-base leading-snug font-semibold">
            Share of total
          </h3>
          <p className="text-muted-foreground text-sm">
            What each chosen product, and the chosen products together, make up
            of everything sold in these dates: {formatCount(totalUnits)} units
            and {formatMoney(totalAmount)}.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <SplitBar
            label="Share of units"
            total={totalUnits}
            rest={rest.units}
            segments={rows.map((row) => ({
              key: row.key,
              name: row.label,
              value: row.units,
              colour: row.colour ?? "",
            }))}
          />
          <SplitBar
            label="Share of sales"
            total={Number(totalAmount)}
            rest={Number(rest.amount)}
            segments={rows.map((row) => ({
              key: row.key,
              name: row.label,
              value: Number(row.amount),
              colour: row.colour ?? "",
            }))}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b text-left">
                <th
                  scope="col"
                  className="text-muted-foreground py-2 pr-3 font-normal"
                >
                  Product
                </th>
                {["Units", "Share of units", "Sales", "Share of sales"].map(
                  (header) => (
                    <th
                      key={header}
                      scope="col"
                      className="text-muted-foreground py-2 pr-3 text-right font-normal whitespace-nowrap last:pr-0"
                    >
                      {header}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Row
                  key={row.key}
                  name={row.label}
                  detail={row.product}
                  colour={row.colour}
                  units={row.units}
                  amount={row.amount}
                  totalUnits={totalUnits}
                  totalAmount={totalAmount}
                />
              ))}
              <Row
                name="Chosen together"
                strong
                colour={null}
                units={together.units}
                amount={together.amount}
                totalUnits={totalUnits}
                totalAmount={totalAmount}
              />
              <Row
                name="All other SKUs"
                muted
                colour={null}
                units={rest.units}
                amount={rest.amount}
                totalUnits={totalUnits}
                totalAmount={totalAmount}
              />
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({
  name,
  detail,
  colour,
  units,
  amount,
  totalUnits,
  totalAmount,
  strong,
  muted,
}: {
  name: string;
  detail?: string;
  colour: string | null;
  units: number;
  amount: string;
  totalUnits: number;
  totalAmount: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <tr
      className={
        strong ? "border-t-2 border-b font-medium" : "border-b last:border-b-0"
      }
    >
      <td className="py-1.5 pr-3">
        <span className="flex items-center gap-2">
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-full"
            style={{ background: colour ?? "transparent" }}
          />
          <span className={muted ? "text-muted-foreground" : undefined}>
            {name}
            {detail ? (
              <span className="text-muted-foreground block text-xs font-normal">
                {detail}
              </span>
            ) : null}
          </span>
        </span>
      </td>
      <td className="py-1.5 pr-3 text-right tabular-nums">
        {formatCount(units)}
      </td>
      <ShareCell text={shareText(units, totalUnits)} />
      <td className="py-1.5 pr-3 text-right whitespace-nowrap tabular-nums">
        {formatMoney(amount)}
      </td>
      <ShareCell text={shareText(Number(amount), Number(totalAmount))} />
    </tr>
  );
}

/** A share as a figure. */
function ShareCell({ text }: { text: string }) {
  return (
    <td className="py-1.5 pr-3 text-right whitespace-nowrap tabular-nums last:pr-0">
      {text}
    </td>
  );
}

/**
 * One bar for the whole — all units, or all sales — cut into the chosen
 * products and, in the muted ink, everything else. The same colours as the
 * lines, so a segment is the product it is drawn as above.
 */
function SplitBar({
  label,
  segments,
  rest,
  total,
}: {
  label: string;
  segments: { key: string; name: string; value: number; colour: string }[];
  rest: number;
  total: number;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs">{label}</span>
      <div
        role="img"
        aria-label={`${label}: ${segments
          .map(
            (segment) => `${segment.name} ${shareText(segment.value, total)}`,
          )
          .join(", ")}, all others ${shareText(rest, total)}`}
        className="bg-muted flex h-3 w-full overflow-hidden rounded-sm"
      >
        {segments.map((segment) => (
          <div
            key={segment.key}
            title={`${segment.name}: ${shareText(segment.value, total)}`}
            style={{
              width: `${String(sharePercent(segment.value, total))}%`,
              background: segment.colour,
            }}
          />
        ))}
      </div>
    </div>
  );
}
